/**
 * Блок задачи внутри карточки байка (§2.2.5).
 *
 * Задача — не «другой объект», а то, что сейчас происходит с этим байком:
 * смотреть на неё отдельно от заряда и замка скауту незачем.
 *
 * ⚠ КНОПКИ «ВЗЯТЬ» ЗДЕСЬ НЕТ. Для невзятой задачи диспетчера она — в шапке
 * карточки (`bike-header.tsx`, проп `action`), рядом с номером и бейджем:
 * так задачу видно сразу, не долистав до этого блока (§2.2.5, дословно
 * «показывается в шапке, а не внизу»). Здесь остаётся только «Выполнено» —
 * действие над уже ВЗЯТОЙ (своей) задачей, ему шапка не нужна.
 */
import {
  completionFor,
  scoutTaskStatusLabel,
  scoutTaskTypeLabel,
  taskReasonText,
  type ScoutTask,
} from '@/api/scout'
import { hubById, type Hub } from '@/api/hubs'
import { distanceM, formatDistance, type LatLng } from '@/lib/geo'
import { navUrl } from '@/lib/navigation'
import { BigButton, Notice } from '@/components/ui'

export function BikeTask({
  task,
  hubs,
  me,
  busy,
  onComplete,
}: {
  task: ScoutTask
  hubs: readonly Hub[] | undefined
  me: LatLng | null
  busy: boolean
  onComplete: () => void
}) {
  const mine = task.status === 'claimed'
  const completion = completionFor(task, me)
  const target = hubById(task.targetZoneId, hubs)
  const targetDistance = me && target ? distanceM(me, target) : null
  const reason = taskReasonText(task)

  return (
    <section className="mb-4 rounded-xl border border-white/15 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-medium">{scoutTaskTypeLabel[task.type] ?? task.type}</h2>
        <span className="shrink-0 text-sm opacity-60">
          {scoutTaskStatusLabel[task.status] ?? task.status}
        </span>
      </div>

      {reason ? <p className="mt-1 text-sm opacity-80">{reason}</p> : null}
      {task.address ? <p className="mt-0.5 text-sm opacity-60">{task.address}</p> : null}

      {/*
        Куда везти. Показывается только там, где у задачи есть цель:
        `targetZoneId` бэкенд требует лишь для `rebalance`.
      */}
      {task.targetZoneId ? (
        <div className="mt-3">
          <h3 className="mb-1 text-xs uppercase tracking-wide opacity-50">Куда везти</h3>
          {target ? (
            <>
              <div className="rounded-lg bg-white/5 px-3 py-2">
                <div className="text-base">{target.name || 'Хаб без названия'}</div>
                {targetDistance !== null ? (
                  <div className="mt-0.5 text-sm opacity-60">
                    {formatDistance(targetDistance)} от вас
                  </div>
                ) : null}
              </div>
              <a
                className="mt-2 flex min-h-[56px] items-center justify-center rounded-xl
                  border border-white/20 px-4 text-center font-medium"
                href={navUrl(target)}
                target="_blank"
                rel="noreferrer"
              >
                Маршрут до хаба
              </a>
            </>
          ) : (
            <Notice tone="warn">
              Хаб-цель не найден в списке парковочных зон. Уточните у диспетчера.
            </Notice>
          )}
        </div>
      ) : null}

      <div className="mt-3">
        {mine ? (
          <>
            <BigButton tone="primary" busy={busy} disabled={completion === null} onClick={onComplete}>
              Выполнено
            </BigButton>
            {completion === null ? (
              <p className="mt-1 text-xs opacity-50">
                Ждём геопозицию — байк встанет на карте там, где вы стоите.
              </p>
            ) : null}
          </>
        ) : task.status === 'open' ? null : (
          <p className="text-sm opacity-60">Задача закрыта — действий по ней нет.</p>
        )}
      </div>
      {mine ? (
        <p className="mt-2 text-xs opacity-50">
          Уйдёт сразу, а без сети — как появится связь.
        </p>
      ) : null}
    </section>
  )
}
